import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { FileText, Play } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChatChart } from "@/components/chat-chart";
import { ChatResultTable } from "@/components/chat-result-table";
import type { ChartSpec } from "@/lib/chat-api";

interface CatalogEntry {
  id: string;
  category: string;
  name: string;
  description: string;
  chartConfig: ChartSpec | null;
}

interface RunResult {
  rows: Record<string, unknown>[];
  sql: string;
  chartConfig: ChartSpec | null;
  name: string;
  description: string;
}

export default function ReportsPage() {
  const [active, setActive] = useState<RunResult | null>(null);

  const { data: catalogData, isLoading: catalogLoading } = useQuery<{
    reports: CatalogEntry[];
  }>({
    queryKey: ["reports-catalog"],
    queryFn: async () => {
      const res = await fetch("/api/reports/catalog", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const runMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/reports/catalog/${id}/run`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()) as RunResult;
    },
    onSuccess: (result) => setActive(result),
  });

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogEntry[]>();
    (catalogData?.reports ?? []).forEach((r) => {
      const list = map.get(r.category) ?? [];
      list.push(r);
      map.set(r.category, list);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalogData]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="h-6 w-6" /> Reports
        </h1>
        <p className="text-sm text-muted-foreground">
          Standard reports run against live BigQuery. Click a report to execute it; in Phase 3 you'll be able to schedule email + Slack delivery.
        </p>
      </div>

      {catalogLoading && <div className="text-muted-foreground">Loading catalog…</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {grouped.map(([category, reports]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle>{category}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {reports.map((r) => (
                <div
                  key={r.id}
                  className="flex items-start justify-between gap-3 border-t pt-3 first:border-0 first:pt-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {r.description}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => runMutation.mutate(r.id)}
                    disabled={runMutation.isPending}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Run
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {runMutation.isPending && (
        <div className="text-sm text-muted-foreground">Running report…</div>
      )}
      {runMutation.isError && (
        <div className="text-sm text-red-600">
          Failed: {String(runMutation.error)}
        </div>
      )}

      {active && (
        <Card>
          <CardHeader>
            <CardTitle>{active.name}</CardTitle>
            <p className="text-xs text-muted-foreground">{active.description}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {active.chartConfig && active.rows.length > 0 && (
              <ChatChart spec={active.chartConfig} rows={active.rows} />
            )}
            <ChatResultTable rows={active.rows} />
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Show SQL</summary>
              <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-auto">
                {active.sql}
              </pre>
            </details>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
